<template>
  <div class="space-y-6">
    <!-- Page Header -->
    <div class="md:flex md:items-center md:justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white">{{ user.full_name }}</h1>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
          User details and management
        </p>
      </div>
      <div class="mt-4 flex space-x-3 md:mt-0 md:ml-4">
        <Link
          :href="route('site-owner.users.index')"
          class="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <ArrowLeftIcon class="h-5 w-5 mr-2" />
          Back to Users
        </Link>
      </div>
    </div>

    <!-- User Information Card -->
    <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="px-4 py-5 sm:p-6">
        <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
          User Information
        </h3>
        <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <!-- Profile Photo -->
          <div class="col-span-2 flex items-center">
            <div class="h-20 w-20 rounded-full bg-gray-300 dark:bg-gray-700 flex items-center justify-center">
              <span class="text-3xl font-medium text-gray-600 dark:text-gray-300">
                {{ user.first_name?.charAt(0) || '' }}{{ user.last_name?.charAt(0) || '' }}
              </span>
            </div>
            <div class="ml-6">
              <h4 class="text-xl font-semibold text-gray-900 dark:text-white">
                {{ user.full_name }}
              </h4>
              <p class="text-sm text-gray-500 dark:text-gray-400">{{ user.email }}</p>
            </div>
          </div>

          <!-- Basic Information -->
          <div>
            <label class="block text-sm font-medium text-gray-500 dark:text-gray-400">First Name</label>
            <p class="mt-1 text-sm text-gray-900 dark:text-white">{{ user.first_name }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-500 dark:text-gray-400">Last Name</label>
            <p class="mt-1 text-sm text-gray-900 dark:text-white">{{ user.last_name }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-500 dark:text-gray-400">Email</label>
            <p class="mt-1 text-sm text-gray-900 dark:text-white">{{ user.email }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-500 dark:text-gray-400">Phone</label>
            <p class="mt-1 text-sm text-gray-900 dark:text-white">{{ user.phone || 'Not provided' }}</p>
          </div>

          <!-- Preferences -->
          <div>
            <label class="block text-sm font-medium text-gray-500 dark:text-gray-400">Language</label>
            <p class="mt-1 text-sm text-gray-900 dark:text-white">{{ user.language || 'Not set' }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-500 dark:text-gray-400">Theme</label>
            <p class="mt-1 text-sm text-gray-900 dark:text-white">{{ user.theme || 'Not set' }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-500 dark:text-gray-400">Timezone</label>
            <p class="mt-1 text-sm text-gray-900 dark:text-white">{{ user.timezone?.name || 'Not set' }}</p>
          </div>

          <!-- Status -->
          <div>
            <label class="block text-sm font-medium text-gray-500 dark:text-gray-400">Account Status</label>
            <p class="mt-1">
              <span
                :class="user.is_active ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'"
                class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
              >
                {{ user.is_active ? 'Active' : 'Inactive' }}
              </span>
            </p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-500 dark:text-gray-400">Site Owner</label>
            <p class="mt-1">
              <span
                :class="user.is_site_owner ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'"
                class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
              >
                {{ user.is_site_owner ? 'Yes' : 'No' }}
              </span>
            </p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-500 dark:text-gray-400">2FA Status</label>
            <p class="mt-1">
              <span
                :class="user.two_fa_enabled ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'"
                class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
              >
                {{ user.two_fa_enabled ? 'Enabled' : 'Disabled' }}
              </span>
              <span v-if="user.two_fa_enabled && user.two_fa_method" class="ml-2 text-xs text-gray-500 dark:text-gray-400">
                ({{ user.two_fa_method }})
              </span>
            </p>
          </div>

          <!-- Timestamps -->
          <div>
            <label class="block text-sm font-medium text-gray-500 dark:text-gray-400">Email Verified</label>
            <p class="mt-1 text-sm text-gray-900 dark:text-white">
              {{ user.email_verified_at ? formatDate(user.email_verified_at) : 'Not verified' }}
            </p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-500 dark:text-gray-400">Last Login</label>
            <p class="mt-1 text-sm text-gray-900 dark:text-white">
              {{ user.last_login_at ? formatDate(user.last_login_at) : 'Never' }}
            </p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-500 dark:text-gray-400">Last Site Owner Login</label>
            <p class="mt-1 text-sm text-gray-900 dark:text-white">
              {{ user.last_site_owner_login_at ? formatDate(user.last_site_owner_login_at) : 'Never' }}
            </p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-500 dark:text-gray-400">Account Created</label>
            <p class="mt-1 text-sm text-gray-900 dark:text-white">{{ formatDate(user.created_at) }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-500 dark:text-gray-400">Last Updated</label>
            <p class="mt-1 text-sm text-gray-900 dark:text-white">{{ formatDate(user.updated_at) }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Companies -->
    <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="px-4 py-5 sm:p-6">
        <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
          Companies ({{ user.companies.length }})
        </h3>
        <div v-if="user.companies.length > 0" class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead class="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Company Name
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Default
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Joined
                </th>
                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              <tr
                v-for="company in user.companies"
                :key="company.id"
                class="hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  {{ company.name }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    :class="company.status === 'active' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'"
                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                  >
                    {{ company.status }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {{ company.is_default ? 'Yes' : 'No' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {{ formatDate(company.joined_at) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <Link
                    :href="route('site-owner.companies.show', company.id)"
                    class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                  >
                    View Company
                  </Link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="text-center py-8">
          <p class="text-sm text-gray-500 dark:text-gray-400">
            This user is not associated with any companies.
          </p>
        </div>
      </div>
    </div>

    <!-- Admin Actions -->
    <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="px-4 py-5 sm:p-6">
        <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
          Admin Actions
        </h3>
        <div class="flex flex-wrap gap-3">
          <!-- Activate/Deactivate -->
          <Link
            v-if="user.is_active"
            :href="route('site-owner.users.deactivate', user.id)"
            method="post"
            as="button"
            class="inline-flex items-center px-4 py-2 border border-red-300 dark:border-red-600 rounded-md shadow-sm text-sm font-medium text-red-700 dark:text-red-300 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <XCircleIcon class="h-5 w-5 mr-2" />
            Deactivate User
          </Link>
          <Link
            v-else
            :href="route('site-owner.users.activate', user.id)"
            method="post"
            as="button"
            class="inline-flex items-center px-4 py-2 border border-green-300 dark:border-green-600 rounded-md shadow-sm text-sm font-medium text-green-700 dark:text-green-300 bg-white dark:bg-gray-800 hover:bg-green-50 dark:hover:bg-green-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <CheckCircleIcon class="h-5 w-5 mr-2" />
            Activate User
          </Link>

          <!-- Grant/Revoke Site Owner -->
          <Link
            v-if="!user.is_site_owner"
            :href="route('site-owner.users.grant-site-owner', user.id)"
            method="post"
            as="button"
            class="inline-flex items-center px-4 py-2 border border-purple-300 dark:border-purple-600 rounded-md shadow-sm text-sm font-medium text-purple-700 dark:text-purple-300 bg-white dark:bg-gray-800 hover:bg-purple-50 dark:hover:bg-purple-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            <ShieldCheckIcon class="h-5 w-5 mr-2" />
            Grant Site Owner
          </Link>
          <Link
            v-else-if="user.id !== $page.props.auth.user.id"
            :href="route('site-owner.users.revoke-site-owner', user.id)"
            method="post"
            as="button"
            class="inline-flex items-center px-4 py-2 border border-orange-300 dark:border-orange-600 rounded-md shadow-sm text-sm font-medium text-orange-700 dark:text-orange-300 bg-white dark:bg-gray-800 hover:bg-orange-50 dark:hover:bg-orange-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
          >
            <ShieldExclamationIcon class="h-5 w-5 mr-2" />
            Revoke Site Owner
          </Link>
        </div>
        <p v-if="user.id === $page.props.auth.user.id" class="mt-3 text-sm text-gray-500 dark:text-gray-400">
          You cannot modify your own account permissions or status.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Link } from '@inertiajs/vue3';
import route from '@/Utils/route';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
} from '@heroicons/vue/24/outline';

const props = defineProps({
  user: {
    type: Object,
    required: true,
  },
});

// Format date
const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
</script>
